import React, { useMemo } from "react";

import { config } from "@app/config";
import { checkConditions, Criteria } from "@app/lib/shopify";
import { useCssVars } from "@app/hooks/use-css-vars";
import { Icon } from "@app/components/icon";

type Position = "top" | "bottom" | "left" | "right";

type Locales = {
  [key: string]: string;
};

type Shop = {
  name: string;
};

export type Props = {
  config?: typeof config;
  locales?: Locales;
  shop?: Shop;
  criteria?: Criteria;
  position?: Position;
  showTooltip?: boolean;
};

/**
 * force
 * Takes a criteria object to pass to checkConditions to validate what badges should display.
 */

export function useBadges(criteria: Criteria) {
  return useMemo(() => {
    const data = window.SHOPIFY_DATA.metaobjects.badges;
    return data
      .filter((badge) => checkConditions(badge.conditions, criteria))
      .sort((a, b) => {
        const priorityA = a.conditions?.priority || 0;
        const priorityB = b.conditions?.priority || 0;
        return priorityB - priorityA;
      });
  }, []);
}

export function Badge({ locales, shop, criteria, position, showTooltip = true }: Props) {
  const [badge] = useBadges(criteria ?? {});
  if (!badge) return null;

  const vars = useCssVars({
    "--background-color": badge.background_color.value,
    "--foreground-color": badge.foreground_color.value,
  });

  return (
    <div
      style={vars}
      className="text-body-xs group relative z-10 flex w-fit items-center gap-1 whitespace-nowrap rounded-md bg-[--background-color] px-2 py-1 font-bold text-[--foreground-color]"
    >
      <span>{badge.title}</span>

      {!badge.subtitle || !showTooltip ? null : (
        <>
          <Icon id="Info" className="size-3" />
          <div className="flex">
            <div className="absolute top-0 z-20 m-auto w-fit rounded-md bg-neutrals-black px-2 py-1 text-white opacity-0 transition-all group-hover:opacity-100 peer-focus:opacity-100 md:left-full md:right-0">
              <div className="h-fit w-60 max-w-max whitespace-break-spaces">{badge.subtitle}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
